import { useRef, useState } from 'react';
import { Mail, Phone, MapPin, Linkedin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { HONEYPOT_FIELD, honeypotInputProps } from '../lib/spam-trap';

const SUPPORT_EMAIL = 'support@adeincorp.com';
const SUPPORT_PHONE = '800.334.1918';
const MAILING_ADDRESS = ['P.O. Box 660', 'Clarkston, MI 48347'];
const LINKEDIN_URL = 'https://www.linkedin.com/company/adeincorp/';

export default function Contact() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', message: '', [HONEYPOT_FIELD]: '' });
  const startedAt = useRef(Date.now());
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, form_ms: Date.now() - startedAt.current }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error === 'validation_error' ? 'Please check your entries and try again.' : 'Something went wrong. Please try again.');
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-to-b from-brand/5 to-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">
            Contact us
          </p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Have a question? We'd love to hear from you.
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            The quickest way to reach us during office hours is by phone or email.
            We typically respond within one business day.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-10">
        {/* Contact info */}
        <div>
          <h2 className="text-xl font-bold mb-6">Reach us</h2>
          <ul className="space-y-5">
            <ContactItem icon={Mail} label="Email">
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand hover:underline">{SUPPORT_EMAIL}</a>
            </ContactItem>
            <ContactItem icon={Phone} label="Phone">
              <a href={`tel:${SUPPORT_PHONE.replace(/\D/g, '')}`} className="text-slate-900">{SUPPORT_PHONE}</a>
            </ContactItem>
            <ContactItem icon={MapPin} label="Mail">
              <address className="not-italic text-slate-700">
                {MAILING_ADDRESS.map((line) => <div key={line}>{line}</div>)}
              </address>
            </ContactItem>
            <ContactItem icon={Linkedin} label="Connect">
              <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                LinkedIn
              </a>
            </ContactItem>
          </ul>

          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-lg p-5">
            <h3 className="font-semibold text-sm">Looking for custom training?</h3>
            <p className="text-sm text-slate-600 mt-1">
              We design and deliver tailored programs for teams, organizations, and
              statewide systems — virtually or on-site. Email us with your needs
              and we'll set up a call.
            </p>
          </div>
        </div>

        {/* Contact form */}
        <div>
          <h2 className="text-xl font-bold mb-6">Send a message</h2>
          {sent ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-emerald-900">
              <CheckCircle className="mb-3" size={28} />
              <h3 className="font-semibold">Message sent</h3>
              <p className="text-sm mt-1">
                Thanks for reaching out! We typically respond within one business day.
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setForm({ first_name: '', last_name: '', email: '', message: '', [HONEYPOT_FIELD]: '' });
                  startedAt.current = Date.now();
                }}
                className="text-sm mt-3 underline"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <input
                {...honeypotInputProps}
                value={form[HONEYPOT_FIELD]}
                onChange={(e) => setForm({ ...form, [HONEYPOT_FIELD]: e.target.value })}
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="First name" required>
                  <input
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="Last name" required>
                  <input
                    type="text"
                    required
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="input"
                  />
                </Field>
              </div>
              <Field label="Email" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Message" required>
                <textarea
                  rows={5}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="input"
                  placeholder="How can we help?"
                />
              </Field>
              {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-rose-800 text-sm flex items-start gap-2">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-dark disabled:opacity-50"
              >
                <Send size={16} /> {submitting ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

function ContactItem({ icon: Icon, label, children }) {
  return (
    <li className="flex gap-4">
      <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </li>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}
